import { NavLink } from "react-router-dom";
import { AlertTriangle, Building2, Gauge, LayoutDashboard, LogOut, Shield, Sliders, UserPlus, Wrench } from "lucide-react";
import { useAuth } from "../../context/AuthContext.js";

/**
 * Class names for a sidebar `NavLink`, matching active vs inactive visual
 * treatment. Moved here from the retired legacy nav tree (`nav/tenant-nav.ts`,
 * deleted under RFC-004 R5.5) — this was its last live export.
 */
function sidebarNavLinkClassName(isActive: boolean): string {
  const base =
    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150";
  return isActive
    ? `${base} bg-app-hover text-app`
    : `${base} text-muted hover:bg-app-hover hover:text-app`;
}

export function AdminSidebar() {
  const { user, logout } = useAuth();

  return (
    <aside className="fixed left-0 top-0 z-50 flex h-screen w-64 flex-col border-r border-card bg-card transition-colors duration-200">
      <div className="border-b border-card px-5 py-4">
        <div className="flex items-center gap-3">
          <Shield className="h-7 w-7 text-[color:var(--brand-500)]" />
          <div>
            <h1 className="text-sm font-bold leading-tight text-app">
              Platform admin
            </h1>
            <p className="font-mono text-[10px] text-subtle">
              Fleet &amp; organizations
            </p>
          </div>
        </div>
      </div>

      <nav className="flex-1 space-y-4 overflow-y-auto px-3 py-3" aria-label="Platform admin">
        <div>
          <h2
            id="admin-nav-section-main"
            className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-widest text-subtle"
          >
            Platform
          </h2>
          <ul className="space-y-0.5" aria-labelledby="admin-nav-section-main">
            <li>
              <NavLink to="/admin" end className={({ isActive }) => sidebarNavLinkClassName(isActive)}>
                <LayoutDashboard className="h-4.5 w-4.5 flex-shrink-0" aria-hidden />
                Overview
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/admin/organizations"
                className={({ isActive }) => sidebarNavLinkClassName(isActive)}
              >
                <Building2 className="h-4.5 w-4.5 flex-shrink-0" aria-hidden />
                Organizations
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/admin/operators"
                className={({ isActive }) => sidebarNavLinkClassName(isActive)}
              >
                <UserPlus className="h-4.5 w-4.5 flex-shrink-0" aria-hidden />
                Tenant operators
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/admin/provider"
                className={({ isActive }) => sidebarNavLinkClassName(isActive)}
              >
                <Gauge className="h-4.5 w-4.5 flex-shrink-0" aria-hidden />
                Provider console
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/admin/defaults"
                className={({ isActive }) => sidebarNavLinkClassName(isActive)}
              >
                <Sliders className="h-4.5 w-4.5 flex-shrink-0" aria-hidden />
                Fleet defaults
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/admin/operations"
                className={({ isActive }) => sidebarNavLinkClassName(isActive)}
              >
                <Wrench className="h-4.5 w-4.5 flex-shrink-0" aria-hidden />
                Operations
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/admin/incidents"
                className={({ isActive }) => sidebarNavLinkClassName(isActive)}
              >
                <AlertTriangle className="h-4.5 w-4.5 flex-shrink-0" aria-hidden />
                Incidents
              </NavLink>
            </li>
          </ul>
        </div>
      </nav>

      <div className="space-y-2 border-t border-card px-4 py-3">
        {user ? (
          <p className="truncate text-[10px] text-subtle" title={user.email}>
            {user.email}
          </p>
        ) : (
          <p className="text-xs text-subtle">Break-glass API key (dev)</p>
        )}
        <button
          type="button"
          className="flex items-center gap-2 text-sm text-muted hover:text-app transition-colors duration-150"
          onClick={() => void logout().then(() => (window.location.href = "/login"))}
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </aside>
  );
}
